import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, Youtube, FileText, Images, Inbox, Plus, Trash2, LogOut, Upload, Loader2, ExternalLink, Home,
  Pencil, Quote, X as XIcon, Save, Film, Link2, Users, Send,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import DatePicker from "@/components/admin/DatePicker";
import { useAuth } from "@/context/AuthContext";
import {
  getEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent,
  getSermons, adminCreateSermon, adminUpdateSermon, adminDeleteSermon,
  getBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog,
  getGallery, adminUploadGallery, adminDeleteGallery, adminUpdateGalleryCaption,
  getTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  adminSubmissions, fileUrl,
  adminGetArchives, adminUploadArchive, adminAddArchiveUrl, adminUpdateArchive, adminDeleteArchive, adminArchiveAccessList,
  adminUploadEventPhoto, adminGetSubscribers, adminAddSubscriber, adminBulkAddSubscribers, adminDeleteSubscriber, adminSendNewsletter,
} from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const input = "w-full bg-black border border-white/20 focus:border-wutn px-3 py-2.5 text-white placeholder:text-zinc-500 outline-none transition-colors text-sm";
const label = "text-xs uppercase tracking-widest text-zinc-500 mb-1 block";
const btn = "inline-flex items-center justify-center gap-2 bg-wutn hover:bg-wutn-dark disabled:opacity-60 text-white px-5 py-2.5 font-cond uppercase tracking-wider text-sm transition-colors";
const btnGhost = "inline-flex items-center gap-2 border border-white/20 hover:border-wutn hover:text-wutn text-zinc-300 px-4 py-2 font-cond uppercase tracking-wider text-xs transition-colors";
const card = "bg-[#0A0A0A] border border-white/10 p-6";

function Section({ title, children, actions }) {
  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <h3 className="font-display uppercase text-2xl">{title}</h3>
        {actions}
      </div>
      {children}
    </div>
  );
}

function RowActions({ onEdit, onDelete, id }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <button onClick={onEdit} className="p-2 text-zinc-500 hover:text-wutn transition-colors" aria-label="Edit" data-testid={`edit-${id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={onDelete} className="p-2 text-zinc-500 hover:text-wutn transition-colors" aria-label="Delete" data-testid={`delete-${id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

// Generic Edit Dialog — renders a children form prefilled, calls onSave with the current form state.
function EditDialog({ open, onOpenChange, title, children, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] p-0 bg-black border border-white/10 rounded-none max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-black z-10">
          <h3 className="font-display uppercase text-2xl">{title}</h3>
          <button onClick={() => onOpenChange(false)} className="p-1.5 text-zinc-500 hover:text-wutn transition-colors" aria-label="Close"><XIcon className="h-5 w-5" /></button>
        </div>
        <div className="p-6">
          {children}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => onOpenChange(false)} className={btnGhost}>Cancel</button>
            <button onClick={onSave} disabled={saving} className={btn} data-testid="edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EVENT_CATEGORIES = ["Preaching & Speaking", "Workshop", "Coaching", "Training"];

// -------------------- FORM FIELDS (module-level to preserve input focus) --------------------
function EventFields({ data, onChange, testPrefix = "event" }) {
  return (
    <div className="space-y-3">
      <div><label className={label}>Title *</label><input className={input} value={data.title} onChange={(e)=>onChange({...data,title:e.target.value})} data-testid={`${testPrefix}-title`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>From Date *</label><DatePicker value={data.date} onChange={(v)=>onChange({...data,date:v})} testId={`${testPrefix}-date`} placeholder="Pick start date" /></div>
        <div><label className={label}>To Date <span className="text-zinc-600 normal-case">(optional)</span></label><DatePicker value={data.end_date||""} onChange={(v)=>onChange({...data,end_date:v})} testId={`${testPrefix}-end-date`} placeholder="Pick end date" /></div>
      </div>
      <div><label className={label}>Time</label><input className={input} placeholder="7:00 PM" value={data.time||""} onChange={(e)=>onChange({...data,time:e.target.value})} data-testid={`${testPrefix}-time`} /></div>
      <div><label className={label}>Location *</label><input className={input} value={data.location} onChange={(e)=>onChange({...data,location:e.target.value})} data-testid={`${testPrefix}-location`} /></div>
      <div>
        <label className={label}>Category</label>
        <select className={input} value={data.category||"Speaking"} onChange={(e)=>onChange({...data,category:e.target.value})} data-testid={`${testPrefix}-category`}>
          {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div><label className={label}>Description</label><textarea rows={3} className={input} value={data.description||""} onChange={(e)=>onChange({...data,description:e.target.value})} data-testid={`${testPrefix}-description`} /></div>
      <div>
        <label className={label}>Photo <span className="text-zinc-600 normal-case">(optional — shown on the event row)</span></label>
        {data.image_url && !data._photoFile && <img src={data.image_url} alt="" className="w-full aspect-video object-cover border border-white/10 mb-2" data-testid={`${testPrefix}-photo-preview`} />}
        <input type="file" accept="image/*" onChange={(e)=>onChange({...data,_photoFile:e.target.files?.[0] || null})} data-testid={`${testPrefix}-photo`} className="block w-full text-sm text-zinc-400 file:mr-3 file:px-4 file:py-2 file:bg-wutn file:text-white file:border-0 file:font-cond file:uppercase file:text-xs file:cursor-pointer" />
        {data._photoFile && <p className="text-xs text-zinc-600 mt-1">{data._photoFile.name} — uploads when you save</p>}
      </div>
    </div>
  );
}

function SermonFields({ data, onChange, testPrefix = "sermon" }) {
  return (
    <div className="space-y-3">
      <div><label className={label}>YouTube Video ID *</label><input className={input} placeholder="e.g. 2AeR1aLK4OY" value={data.youtube_id} onChange={(e)=>onChange({...data,youtube_id:e.target.value})} data-testid={`${testPrefix}-ytid`} /><p className="text-xs text-zinc-600 mt-1">Grab the 11-character ID after v= in a YouTube URL.</p></div>
      <div><label className={label}>Title *</label><input className={input} value={data.title} onChange={(e)=>onChange({...data,title:e.target.value})} data-testid={`${testPrefix}-title`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>Series</label><input className={input} value={data.series||""} onChange={(e)=>onChange({...data,series:e.target.value})} data-testid={`${testPrefix}-series`} /></div>
        <div><label className={label}>Date *</label><DatePicker value={data.date} onChange={(v)=>onChange({...data,date:v})} testId={`${testPrefix}-date`} placeholder="Pick date" /></div>
      </div>
      <div><label className={label}>Speaker</label><input className={input} value={data.speaker||""} onChange={(e)=>onChange({...data,speaker:e.target.value})} data-testid={`${testPrefix}-speaker`} /></div>
    </div>
  );
}

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function BlogFields({ data, onChange, testPrefix = "blog" }) {
  return (
    <div className="space-y-3">
      <div><label className={label}>Title *</label><input className={input} value={data.title} onChange={(e)=>onChange({...data,title:e.target.value})} data-testid={`${testPrefix}-title`} /></div>
      <div><label className={label}>Slug (auto if blank)</label><input className={input} value={data.slug||""} onChange={(e)=>onChange({...data,slug:e.target.value})} placeholder={data.title ? slugify(data.title) : "my-article"} data-testid={`${testPrefix}-slug`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>Category</label><input className={input} value={data.category||""} onChange={(e)=>onChange({...data,category:e.target.value})} data-testid={`${testPrefix}-category`} /></div>
        <div><label className={label}>Date *</label><DatePicker value={data.date} onChange={(v)=>onChange({...data,date:v})} testId={`${testPrefix}-date`} placeholder="Pick date" /></div>
      </div>
      <div><label className={label}>Excerpt</label><input className={input} value={data.excerpt||""} onChange={(e)=>onChange({...data,excerpt:e.target.value})} data-testid={`${testPrefix}-excerpt`} /></div>
      <div><label className={label}>Content * (blank line between paragraphs)</label><textarea rows={8} className={input} value={data.content||""} onChange={(e)=>onChange({...data,content:e.target.value})} data-testid={`${testPrefix}-content`} /></div>
    </div>
  );
}

function TestimonialFields({ data, onChange, testPrefix = "testimonial" }) {
  return (
    <div className="space-y-3">
      <div><label className={label}>Author Name *</label><input className={input} value={data.author} onChange={(e)=>onChange({...data,author:e.target.value})} data-testid={`${testPrefix}-author`} placeholder="e.g. Pastor John Doe" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>Role</label><input className={input} value={data.role||""} onChange={(e)=>onChange({...data,role:e.target.value})} data-testid={`${testPrefix}-role`} placeholder="Pastor / Youth Pastor" /></div>
        <div><label className={label}>Organization</label><input className={input} value={data.organization||""} onChange={(e)=>onChange({...data,organization:e.target.value})} data-testid={`${testPrefix}-org`} placeholder="Grace Church" /></div>
      </div>
      <div><label className={label}>Location</label><input className={input} value={data.location||""} onChange={(e)=>onChange({...data,location:e.target.value})} data-testid={`${testPrefix}-location`} placeholder="North Bay, ON" /></div>
      <div><label className={label}>Quote *</label><textarea rows={4} className={input} value={data.quote} onChange={(e)=>onChange({...data,quote:e.target.value})} data-testid={`${testPrefix}-quote`} placeholder="Kerby's visit transformed our youth group..." /></div>
      <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-400"><input type="checkbox" checked={!!data.featured} onChange={(e)=>onChange({...data,featured:e.target.checked})} data-testid={`${testPrefix}-featured`} /> Featured</label>
    </div>
  );
}


// -------------------- EVENTS --------------------
const EMPTY_EVENT = { title: "", date: "", end_date: "", time: "", location: "", category: "Speaking", description: "", image_url: "" };

function EventsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, form}

  const load = () => getEvents().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.location) return toast.error("Title, date, and location are required");
    setSaving(true);
    try {
      const { _photoFile, ...payload } = form;
      const created = await adminCreateEvent(payload);
      if (_photoFile && created?.id) {
        try { await adminUploadEventPhoto(created.id, _photoFile); }
        catch { toast.error("Event saved, but the photo failed to upload — re-add it from Edit"); }
      }
      toast.success("Event added"); setForm(EMPTY_EVENT); load();
    }
    catch { toast.error("Could not save event"); } finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const { _photoFile, ...payload } = editing.form;
      await adminUpdateEvent(editing.id, payload);
      if (_photoFile) {
        try { await adminUploadEventPhoto(editing.id, _photoFile); }
        catch { toast.error("Event saved, but the photo failed to upload"); }
      }
      toast.success("Event updated"); setEditing(null); load();
    }
    catch { toast.error("Update failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await adminDeleteEvent(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Section title="Add Event">
          <form onSubmit={create} data-testid="event-form">
            <EventFields data={form} onChange={setForm} testPrefix="event-new" />
            <button disabled={saving} className={`${btn} mt-3`} data-testid="event-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Event</button>
          </form>
        </Section>
      </div>
      <div className="lg:col-span-3">
        <Section title={`Events (${items.length})`}>
          {items.length === 0 ? <p className="text-zinc-500 text-sm">No events yet.</p> : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`event-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-cond uppercase text-white truncate">{it.title}</div>
                <div className="text-xs text-zinc-500 truncate">{it.date}{it.end_date ? ` → ${it.end_date}` : ""} · {it.time || "—"} · {it.location}</div>
              </div>
              <RowActions id={it.id} onEdit={() => setEditing({ id: it.id, form: { ...EMPTY_EVENT, ...it } })} onDelete={() => remove(it.id)} />
            </div>
          ))}
        </Section>
      </div>
      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title="Edit Event" onSave={save} saving={saving}>
        {editing && <EventFields data={editing.form} onChange={(f) => setEditing({ ...editing, form: f })} testPrefix="event-edit" />}
      </EditDialog>
    </div>
  );
}

// -------------------- SERMONS --------------------
const EMPTY_SERMON = { youtube_id: "", title: "", series: "Message", speaker: "Kerby Stivene", date: "", featured: false };

function SermonsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_SERMON);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => getSermons().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.youtube_id || !form.title || !form.date) return toast.error("YouTube ID, title, and date are required");
    setSaving(true);
    try { await adminCreateSermon(form); toast.success("Sermon added"); setForm(EMPTY_SERMON); load(); }
    catch { toast.error("Could not save sermon"); } finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await adminUpdateSermon(editing.id, editing.form); toast.success("Sermon updated"); setEditing(null); load(); }
    catch { toast.error("Update failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    try { await adminDeleteSermon(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Section title="Add Sermon">
          <form onSubmit={create} data-testid="sermon-form">
            <SermonFields data={form} onChange={setForm} testPrefix="sermon-new" />
            <button disabled={saving} className={`${btn} mt-3`} data-testid="sermon-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Sermon</button>
          </form>
        </Section>
      </div>
      <div className="lg:col-span-3">
        <Section title={`Sermons (${items.length})`}>
          {items.length === 0 ? <p className="text-zinc-500 text-sm">No sermons yet.</p> : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`sermon-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-cond uppercase text-white truncate">{it.title}</div>
                <div className="text-xs text-zinc-500 truncate">{it.series} · {it.date} · <a className="text-wutn hover:underline" href={`https://youtu.be/${it.youtube_id}`} target="_blank" rel="noreferrer">{it.youtube_id}</a></div>
              </div>
              <RowActions id={it.id} onEdit={() => setEditing({ id: it.id, form: { ...EMPTY_SERMON, ...it } })} onDelete={() => remove(it.id)} />
            </div>
          ))}
        </Section>
      </div>
      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title="Edit Sermon" onSave={save} saving={saving}>
        {editing && <SermonFields data={editing.form} onChange={(f) => setEditing({ ...editing, form: f })} testPrefix="sermon-edit" />}
      </EditDialog>
    </div>
  );
}

// -------------------- BLOG --------------------
const EMPTY_BLOG = { title: "", slug: "", excerpt: "", content: "", category: "Article", author: "Kerby Stivene", date: "", read_time: "5 min" };

function BlogPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_BLOG);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => getBlog().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const slug = form.slug || slugify(form.title);
    if (!form.title || !slug || !form.content || !form.date) return toast.error("Title, content, and date are required");
    setSaving(true);
    try { await adminCreateBlog({ ...form, slug }); toast.success("Article added"); setForm(EMPTY_BLOG); load(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Could not save article"); }
    finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const slug = editing.form.slug || slugify(editing.form.title);
      await adminUpdateBlog(editing.id, { ...editing.form, slug });
      toast.success("Article updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await adminDeleteBlog(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Section title="Add Article">
          <form onSubmit={create} data-testid="blog-form">
            <BlogFields data={form} onChange={setForm} testPrefix="blog-new" />
            <button disabled={saving} className={`${btn} mt-3`} data-testid="blog-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish</button>
          </form>
        </Section>
      </div>
      <div className="lg:col-span-3">
        <Section title={`Articles (${items.length})`}>
          {items.length === 0 ? <p className="text-zinc-500 text-sm">No articles yet.</p> : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`blog-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-cond uppercase text-white truncate">{it.title}</div>
                <div className="text-xs text-zinc-500 truncate">{it.category} · {it.date} · <Link className="text-wutn hover:underline" to={`/blog/${it.slug}`}>view</Link></div>
              </div>
              <RowActions id={it.id} onEdit={() => setEditing({ id: it.id, form: { ...EMPTY_BLOG, ...it } })} onDelete={() => remove(it.id)} />
            </div>
          ))}
        </Section>
      </div>
      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title="Edit Article" onSave={save} saving={saving}>
        {editing && <BlogFields data={editing.form} onChange={(f) => setEditing({ ...editing, form: f })} testPrefix="blog-edit" />}
      </EditDialog>
    </div>
  );
}

// -------------------- TESTIMONIALS --------------------
const EMPTY_TESTIMONIAL = { author: "", role: "", organization: "", location: "", quote: "", featured: false };

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => getTestimonials().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.author || !form.quote) return toast.error("Author and quote are required");
    setSaving(true);
    try { await adminCreateTestimonial(form); toast.success("Testimonial added"); setForm(EMPTY_TESTIMONIAL); load(); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await adminUpdateTestimonial(editing.id, editing.form); toast.success("Updated"); setEditing(null); load(); }
    catch { toast.error("Update failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await adminDeleteTestimonial(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Section title="Add Testimonial">
          <form onSubmit={create} data-testid="testimonial-form">
            <TestimonialFields data={form} onChange={setForm} testPrefix="testimonial-new" />
            <button disabled={saving} className={`${btn} mt-3`} data-testid="testimonial-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add</button>
          </form>
        </Section>
      </div>
      <div className="lg:col-span-3">
        <Section title={`Testimonials (${items.length})`}>
          {items.length === 0 ? <p className="text-zinc-500 text-sm">No testimonials yet.</p> : items.map((it) => (
            <div key={it.id} className="flex items-start justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`testimonial-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-cond uppercase text-white">{it.author}{it.featured && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-wutn/20 text-wutn">FEATURED</span>}</div>
                <div className="text-xs text-zinc-500">{[it.role, it.organization, it.location].filter(Boolean).join(" · ") || "—"}</div>
                <div className="text-sm text-zinc-300 mt-1 line-clamp-2">“{it.quote}”</div>
              </div>
              <RowActions id={it.id} onEdit={() => setEditing({ id: it.id, form: { ...EMPTY_TESTIMONIAL, ...it } })} onDelete={() => remove(it.id)} />
            </div>
          ))}
        </Section>
      </div>
      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title="Edit Testimonial" onSave={save} saving={saving}>
        {editing && <TestimonialFields data={editing.form} onChange={(f) => setEditing({ ...editing, form: f })} testPrefix="testimonial-edit" />}
      </EditDialog>
    </div>
  );
}

// -------------------- GALLERY --------------------
function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => getGallery().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { await adminUploadGallery(f, caption); }
      toast.success(`${files.length} image(s) uploaded`); setCaption(""); e.target.value = ""; load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try { await adminDeleteGallery(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };
  const renameCaption = async (it) => {
    const next = window.prompt("Edit caption:", it.caption || "");
    if (next === null) return; // cancelled
    try { await adminUpdateGalleryCaption(it.id, next); toast.success("Caption updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <Section title={`Gallery (${items.length})`} actions={
      <div className="flex items-center gap-3 flex-wrap">
        <input value={caption} onChange={(e)=>setCaption(e.target.value)} placeholder="Caption (optional)" className={`${input} max-w-[360px]`} data-testid="gallery-caption" />
        <label className={`${btn} cursor-pointer`} data-testid="gallery-upload-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>Upload</span>
          <input type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid="gallery-upload-input" />
        </label>
      </div>
    }>
      {items.length === 0 ? <p className="text-zinc-500 text-sm">No images yet. Upload your first photo.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((it) => (
            <div key={it.id} className="relative group border border-white/10" data-testid={`gallery-item-${it.id}`}>
              <img src={fileUrl(it.storage_path)} alt={it.caption || ""} className="w-full aspect-square object-cover" />
              {it.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-xs text-white px-2 py-1 truncate">{it.caption}</div>}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => renameCaption(it)} className="bg-black/70 text-white p-2 hover:bg-wutn" aria-label="Edit caption" data-testid={`gallery-edit-${it.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(it.id)} className="bg-black/70 text-white p-2 hover:bg-wutn" aria-label="Delete" data-testid={`gallery-delete-${it.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// -------------------- ARCHIVES (video uploads) --------------------
function captureVideoThumb(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.muted = true; video.playsInline = true; video.preload = "auto"; video.src = url;
      const finish = (blob) => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(blob); };
      const timer = setTimeout(() => finish(null), 10000);
      video.addEventListener("loadedmetadata", () => {
        const d = video.duration || 2;
        video.currentTime = Math.min(1.5, d / 4);
      });
      video.addEventListener("seeked", () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = Math.min(1, 640 / (video.videoWidth || 640));
          canvas.width = Math.max(1, Math.round((video.videoWidth || 640) * scale));
          canvas.height = Math.max(1, Math.round((video.videoHeight || 360) * scale));
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => finish(b), "image/jpeg", 0.8);
        } catch { finish(null); }
      });
      video.addEventListener("error", () => finish(null));
    } catch { resolve(null); }
  });
}

function ArchivesPanel() {
  const [items, setItems] = useState([]);
  const [access, setAccess] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", thumbnail_url: "", video_url: "" });
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => {
    adminGetArchives().then(setItems).catch(() => {});
    adminArchiveAccessList().then(setAccess).catch(() => {});
    getSermons().then(setSermons).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Give it a title");
    if (mode === "url") {
      if (!form.video_url) return toast.error("Paste a YouTube or Google Drive link");
      setUploading(true);
      try {
        await adminAddArchiveUrl(form);
        toast.success("Video added");
        setForm({ title: "", description: "", thumbnail_url: "", video_url: "" });
        load();
      } catch (err) { toast.error(err.response?.data?.detail || "Could not add video"); }
      finally { setUploading(false); }
      return;
    }
    if (!file) return toast.error("Choose a video file");
    setUploading(true); setProgress(0);
    try {
      await adminUploadArchive(file, form, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      }, form.thumbnail_url ? null : thumb);
      toast.success("Video uploaded");
      setForm({ title: "", description: "", thumbnail_url: "", video_url: "" }); setFile(null); setThumb(null); setThumbPreview(null); setProgress(0);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f); setThumb(null); setThumbPreview(null);
    if (f) {
      const blob = await captureVideoThumb(f);
      if (blob) { setThumb(blob); setThumbPreview(URL.createObjectURL(blob)); }
    }
  };
  const saveEdit = async () => {
    const chapters = (editing.chapters_text || "")
      .split("\n").map((line) => line.trim()).filter(Boolean)
      .map((line) => {
        const m = line.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(.+)$/);
        if (!m) return null;
        const [, a, b, c, title] = m;
        const sec = c ? (parseInt(a)*3600 + parseInt(b)*60 + parseInt(c)) : (parseInt(a)*60 + parseInt(b));
        return { title: title.trim(), time_seconds: sec };
      }).filter(Boolean);
    try {
      await adminUpdateArchive(editing.id, {
        title: editing.title,
        description: editing.description || "",
        thumbnail_url: editing.thumbnail_url || "",
        chapters,
      });
      toast.success("Updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await adminDeleteArchive(id); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const modeBtn = (active) => `flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 font-cond uppercase text-xs tracking-wider border transition-colors ${active ? "bg-wutn border-wutn text-white" : "border-white/15 text-zinc-400 hover:border-wutn hover:text-white"}`;

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <Section title={mode === "file" ? "Upload Video" : "Add Video From URL"}>
            <form onSubmit={submit} data-testid="archive-form" className="space-y-3">
              <div className="flex gap-2">
                <button type="button" onClick={()=>setMode("file")} data-testid="archive-mode-file" className={modeBtn(mode==="file")}><Upload className="h-3.5 w-3.5" /> Upload File</button>
                <button type="button" onClick={()=>setMode("url")} data-testid="archive-mode-url" className={modeBtn(mode==="url")}><Link2 className="h-3.5 w-3.5" /> YouTube / Drive</button>
              </div>
              {mode === "file" ? (
                <div><label className={label}>Video File * (mp4/mov/webm, up to 500MB)</label>
                  <input type="file" accept="video/*" onChange={onPickFile} data-testid="archive-file" className="block w-full text-sm text-zinc-400 file:mr-3 file:px-4 file:py-2 file:bg-wutn file:text-white file:border-0 file:font-cond file:uppercase file:text-xs file:cursor-pointer" />
                  {file && <p className="text-xs text-zinc-600 mt-1">{file.name} · {(file.size/1024/1024).toFixed(1)} MB</p>}
                  {thumbPreview && !form.thumbnail_url && (
                    <div className="mt-2">
                      <img src={thumbPreview} alt="Auto-captured thumbnail" className="w-full aspect-video object-cover border border-white/10" data-testid="archive-thumb-preview" />
                      <p className="text-xs text-zinc-600 mt-1">Thumbnail auto-captured from the video — paste an image URL below to override.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div><label className={label}>Video URL * (YouTube or Google Drive)</label>
                  <input className={input} value={form.video_url} onChange={(e)=>setForm({...form,video_url:e.target.value})} data-testid="archive-url" placeholder="https://youtube.com/watch?v=… or drive.google.com/file/d/…" />
                  <p className="text-xs text-zinc-600 mt-1">YouTube thumbnails are pulled automatically. For Drive, set sharing to "Anyone with the link".</p>
                </div>
              )}
              {mode === "url" && sermons.length > 0 && (
                <div>
                  <label className={label}>Or pick from your YouTube sermons</label>
                  <select className={input} value="" data-testid="archive-sermon-pick" onChange={(e)=>{
                    const s = sermons.find((x) => x.youtube_id === e.target.value);
                    if (s) setForm({ ...form, video_url: `https://www.youtube.com/watch?v=${s.youtube_id}`, title: form.title || s.title });
                  }}>
                    <option value="">Choose a sermon…</option>
                    {sermons.map((s) => <option key={s.id || s.youtube_id} value={s.youtube_id}>{s.title}{s.date ? ` (${s.date})` : ""}</option>)}
                  </select>
                  <p className="text-xs text-zinc-600 mt-1">Fills the link and title for you — then hit Add Video.</p>
                </div>
              )}
              <div><label className={label}>Title *</label><input className={input} value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} data-testid="archive-title" /></div>
              <div><label className={label}>Description</label><textarea rows={3} className={input} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} data-testid="archive-description" /></div>
              <div><label className={label}>Thumbnail Image URL (optional)</label><input className={input} value={form.thumbnail_url} onChange={(e)=>setForm({...form,thumbnail_url:e.target.value})} data-testid="archive-thumbnail" placeholder="https://..." /></div>
              {uploading && mode === "file" && (
                <div>
                  <div className="h-2 bg-white/10"><div className="h-full bg-wutn transition-all" style={{width: `${progress}%`}} /></div>
                  <p className="text-xs text-zinc-500 mt-2">Uploading… {progress}%</p>
                </div>
              )}
              <button disabled={uploading} className={`${btn} w-full`} data-testid="archive-submit">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "file" ? <Upload className="h-4 w-4" /> : <Link2 className="h-4 w-4" />)} {mode === "file" ? "Upload Video" : "Add Video"}
              </button>
            </form>
          </Section>
        </div>
        <div className="lg:col-span-3">
          <Section title={`Videos (${items.length})`}>
            {items.length === 0 ? <p className="text-zinc-500 text-sm">No videos yet.</p> : items.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`archive-row-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-cond uppercase text-white truncate">{it.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{it.source_type === "youtube" ? "YouTube embed" : it.source_type === "gdrive" ? "Google Drive embed" : `${(it.size/1024/1024).toFixed(1)} MB · ${it.content_type}`}</div>
                </div>
                <RowActions id={it.id} onEdit={() => setEditing({ ...it, chapters_text: (it.chapters || []).map((c) => `${String(Math.floor(c.time_seconds/60)).padStart(2,"0")}:${String(c.time_seconds%60).padStart(2,"0")} ${c.title}`).join("\n") })} onDelete={() => remove(it.id)} />
              </div>
            ))}
          </Section>
        </div>
      </div>

      <Section title={`Access Signups (${access.length})`}>
        {access.length === 0 ? <p className="text-zinc-500 text-sm">No signups yet.</p> : (
          <div className="divide-y divide-white/5">
            {access.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <span className="text-white">{a.name || "—"}</span>
                  <span className="text-zinc-500 ml-2">{a.email}</span>
                </div>
                <span className="text-xs text-zinc-600">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} title="Edit Video" onSave={saveEdit} saving={false}>
        {editing && (
          <div className="space-y-3">
            <div><label className={label}>Title *</label><input className={input} value={editing.title} onChange={(e)=>setEditing({...editing,title:e.target.value})} data-testid="archive-edit-title" /></div>
            <div><label className={label}>Description</label><textarea rows={3} className={input} value={editing.description||""} onChange={(e)=>setEditing({...editing,description:e.target.value})} data-testid="archive-edit-description" /></div>
            <div><label className={label}>Thumbnail URL</label><input className={input} value={editing.thumbnail_url||""} onChange={(e)=>setEditing({...editing,thumbnail_url:e.target.value})} data-testid="archive-edit-thumbnail" /></div>
            <div>
              <label className={label}>Chapters (one per line: <span className="text-zinc-400 normal-case">MM:SS Chapter title</span>)</label>
              <textarea rows={6} className={`${input} font-mono`} value={editing.chapters_text||""} onChange={(e)=>setEditing({...editing,chapters_text:e.target.value})} data-testid="archive-edit-chapters" placeholder={"00:00 Intro\n04:30 The Call\n18:45 Closing prayer"} />
            </div>
          </div>
        )}
      </EditDialog>
    </div>
  );
}

// -------------------- SUBSCRIBERS --------------------
function BlastForm({ items }) {
  const [form, setForm] = useState({ subject: "", message: "", tag: "" });
  const [sending, setSending] = useState(false);
  const allTags = [...new Set(items.flatMap((s) => s.tags || []))];
  const audience = form.tag ? items.filter((s) => (s.tags || []).includes(form.tag)) : items;

  const send = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Subject and message are required");
    if (!audience.length) return toast.error("No subscribers in that audience");
    if (!window.confirm(`Send "${form.subject}" to ${audience.length} subscriber${audience.length === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const r = await adminSendNewsletter(form);
      toast.success(`Sent to ${r.sent} of ${r.total}${r.failed ? ` — ${r.failed} failed` : ""}`);
      setForm({ subject: "", message: "", tag: form.tag });
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <Section title="Send Newsletter">
      <form onSubmit={send} className="space-y-3" data-testid="blast-form">
        <div><label className={label}>Audience</label>
          <select className={input} value={form.tag} onChange={(e)=>setForm({...form,tag:e.target.value})} data-testid="blast-audience">
            <option value="">All subscribers ({items.length})</option>
            {allTags.map((t) => <option key={t} value={t}>{t} ({items.filter((s)=>(s.tags||[]).includes(t)).length})</option>)}
          </select>
        </div>
        <div><label className={label}>Subject *</label><input className={input} value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})} data-testid="blast-subject" placeholder="This Sunday at Wake-Up The Nation" /></div>
        <div><label className={label}>Message * <span className="text-zinc-600 normal-case">(blank line = new paragraph)</span></label><textarea rows={8} className={input} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} data-testid="blast-message" placeholder={"Saints,\n\nWe're excited to announce..."} /></div>
        <button disabled={sending || !audience.length} className={`${btn} w-full`} data-testid="blast-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to {audience.length} Subscriber{audience.length === 1 ? "" : "s"}
        </button>
      </form>
    </Section>
  );
}

function SubscribersPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", tags: "" });
  const [bulk, setBulk] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const load = () => adminGetSubscribers().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const parseTags = (s) => (s || "").split(",").map((t) => t.trim()).filter(Boolean);

  const addOne = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Email is required");
    setSaving(true);
    try {
      const r = await adminAddSubscriber({ email: form.email, name: form.name, tags: parseTags(form.tags) });
      toast.success(r.status === "updated" ? "Existing subscriber updated" : "Subscriber added");
      setForm({ email: "", name: "", tags: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add subscriber — check the email"); }
    finally { setSaving(false); }
  };

  const addBulk = async (e) => {
    e.preventDefault();
    const entries = bulk.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const [email, name, tags] = line.split(",").map((x) => (x || "").trim());
      return { email, name: name || "", tags: tags ? tags.split(/[;|]/).map((t) => t.trim()).filter(Boolean) : [] };
    }).filter((x) => x.email);
    if (!entries.length) return toast.error("Paste at least one line: email, name, tags");
    setSaving(true);
    try {
      const r = await adminBulkAddSubscribers(entries);
      toast.success(`Done — ${r.added} added, ${r.updated} updated${r.invalid ? `, ${r.invalid} skipped (bad email)` : ""}`);
      setBulk(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk add failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try { await adminDeleteSubscriber(id); toast.success("Removed"); load(); } catch { toast.error("Remove failed"); }
  };

  const q = filter.toLowerCase();
  const visible = items.filter((s) => !q || (s.email || "").toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q) || (s.tags || []).some((t) => t.toLowerCase().includes(q)));
  const allTags = [...new Set(items.flatMap((s) => s.tags || []))];

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Section title="Add Subscriber">
          <form onSubmit={addOne} className="space-y-3" data-testid="subscriber-form">
            <div><label className={label}>Email *</label><input className={input} type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="subscriber-email" /></div>
            <div><label className={label}>Name</label><input className={input} value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="subscriber-name" /></div>
            <div><label className={label}>Tags <span className="text-zinc-600 normal-case">(comma separated)</span></label><input className={input} value={form.tags} onChange={(e)=>setForm({...form,tags:e.target.value})} placeholder="youth, camp 2026, pastors" data-testid="subscriber-tags" /></div>
            <button disabled={saving} className={`${btn} w-full`} data-testid="subscriber-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Subscriber</button>
          </form>
        </Section>
        <Section title="Bulk Add">
          <form onSubmit={addBulk} className="space-y-3">
            <textarea rows={6} className={`${input} font-mono text-xs`} value={bulk} onChange={(e)=>setBulk(e.target.value)} data-testid="subscriber-bulk" placeholder={"jane@example.com, Jane Doe, youth;camp\njohn@example.com\nmarc@example.com, Marc L, pastors"} />
            <p className="text-xs text-zinc-600">One per line: <span className="text-zinc-400">email, name (optional), tags separated by ;</span> — existing emails get their tags merged, nothing is duplicated.</p>
            <button disabled={saving} className={`${btn} w-full`} data-testid="subscriber-bulk-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import List</button>
          </form>
        </Section>
        <BlastForm items={items} />
      </div>
      <div className="lg:col-span-3">
        <Section title={`Subscribers (${items.length})`}>
          <input className={`${input} mb-3`} value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder="Filter by email, name or tag…" data-testid="subscriber-filter" />
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {allTags.map((t) => (
                <button key={t} type="button" onClick={()=>setFilter(t)} data-testid={`subscriber-tag-${t}`} className={`text-[10px] px-2 py-1 font-cond uppercase tracking-wider border transition-colors ${filter===t ? "bg-wutn border-wutn text-white" : "border-white/15 text-zinc-400 hover:border-wutn hover:text-white"}`}>{t}</button>
              ))}
            </div>
          )}
          {visible.length === 0 ? <p className="text-zinc-500 text-sm">{items.length === 0 ? "No subscribers yet." : "No matches."}</p> : visible.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`subscriber-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-200 truncate">{s.email}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {s.name || "—"} · {s.source === "admin" ? "added by you" : "joined via site"}
                  {(s.tags || []).length > 0 && <> · {(s.tags || []).map((t) => <span key={t} className="inline-block ml-1 px-1.5 py-0.5 bg-wutn/15 text-wutn text-[10px] font-cond uppercase tracking-wider">{t}</span>)}</>}
                </div>
              </div>
              <button onClick={() => remove(s.id)} className="p-2 text-zinc-500 hover:text-wutn transition-colors" data-testid={`subscriber-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

// -------------------- SUBMISSIONS --------------------
function SubmissionsPanel() {
  const [data, setData] = useState({ contacts: [], prayers: [], bookings: [] });
  useEffect(() => { adminSubmissions().then(setData).catch(() => {}); }, []);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title={`Speaking Requests (${data.bookings.length})`}>
        {data.bookings.length === 0 ? <p className="text-zinc-500 text-sm">No requests yet.</p> : data.bookings.map((b) => (
          <div key={b.id} className="py-3 border-b border-white/5 last:border-0" data-testid={`booking-${b.id}`}>
            <div className="font-cond uppercase text-white">{b.organization}</div>
            <div className="text-xs text-zinc-500">{b.name} · <a className="text-wutn hover:underline" href={`mailto:${b.email}`}>{b.email}</a>{b.phone ? ` · ${b.phone}` : ""}</div>
            <div className="text-xs text-zinc-400 mt-1">{b.event_date} · {b.location} · {(b.services||[]).join(", ") || "—"} · {b.audience_size || "—"}</div>
            {b.message && <div className="text-sm text-zinc-300 mt-2">{b.message}</div>}
            <div className="text-[10px] text-zinc-600 mt-2">{fmt(b.created_at)}</div>
          </div>
        ))}
      </Section>
      <Section title={`Prayer Requests (${data.prayers.length})`}>
        {data.prayers.length === 0 ? <p className="text-zinc-500 text-sm">No prayer requests yet.</p> : data.prayers.map((p) => (
          <div key={p.id} className="py-3 border-b border-white/5 last:border-0">
            <div className="font-cond uppercase text-white">{p.name}{p.is_private && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-wutn/20 text-wutn">PRIVATE</span>}</div>
            <div className="text-xs text-zinc-500">{p.email || "no email"}</div>
            <div className="text-sm text-zinc-300 mt-1">{p.request}</div>
            <div className="text-[10px] text-zinc-600 mt-2">{fmt(p.created_at)}</div>
          </div>
        ))}
      </Section>
      <Section title={`Contact Messages (${data.contacts.length})`}>
        {data.contacts.length === 0 ? <p className="text-zinc-500 text-sm">No messages yet.</p> : data.contacts.map((c) => (
          <div key={c.id} className="py-3 border-b border-white/5 last:border-0">
            <div className="font-cond uppercase text-white">{c.subject || "General"}</div>
            <div className="text-xs text-zinc-500">{c.name} · <a className="text-wutn hover:underline" href={`mailto:${c.email}`}>{c.email}</a></div>
            <div className="text-sm text-zinc-300 mt-1">{c.message}</div>
            <div className="text-[10px] text-zinc-600 mt-2">{fmt(c.created_at)}</div>
          </div>
        ))}
      </Section>
    </div>
  );
}

// -------------------- DASHBOARD --------------------
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const tabCls = "rounded-none data-[state=active]:bg-wutn data-[state=active]:text-white font-cond uppercase tracking-wider text-xs px-5 py-2.5";
  return (
    <div className="min-h-screen bg-black">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logoIcon} alt="WUTN" className="h-10 w-10 object-contain" />
            <div className="hidden sm:block">
              <div className="font-cond uppercase tracking-widest text-xs text-wutn">Admin</div>
              <div className="font-display uppercase text-lg leading-none">Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className={btnGhost} data-testid="admin-view-site"><Home className="h-4 w-4" /> View Site</Link>
            {user?.email && <span className="hidden md:inline text-xs text-zinc-500 px-3">{user.email}</span>}
            <button onClick={logout} className={btnGhost} data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign Out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
        <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.9] mb-8">Manage <span className="text-wutn">Your Site</span></h1>
        <Tabs defaultValue="events" className="space-y-6">
          <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-none p-1 flex flex-nowrap gap-1 h-auto overflow-x-auto w-full justify-start" style={{scrollbarWidth: "none"}}>
            <TabsTrigger value="events" className={`${tabCls} shrink-0`} data-testid="tab-events"><Calendar className="h-4 w-4 mr-2" /> Events</TabsTrigger>
            <TabsTrigger value="archives" className={`${tabCls} shrink-0`} data-testid="tab-archives"><Film className="h-4 w-4 mr-2" /> Archives</TabsTrigger>
            <TabsTrigger value="blog" className={`${tabCls} shrink-0`} data-testid="tab-blog"><FileText className="h-4 w-4 mr-2" /> Blog</TabsTrigger>
            <TabsTrigger value="testimonials" className={`${tabCls} shrink-0`} data-testid="tab-testimonials"><Quote className="h-4 w-4 mr-2" /> Testimonials</TabsTrigger>
            <TabsTrigger value="gallery" className={`${tabCls} shrink-0`} data-testid="tab-gallery"><Images className="h-4 w-4 mr-2" /> Gallery</TabsTrigger>
            <TabsTrigger value="subscribers" className={`${tabCls} shrink-0`} data-testid="tab-subscribers"><Users className="h-4 w-4 mr-2" /> Subscribers</TabsTrigger>
            <TabsTrigger value="submissions" className={`${tabCls} shrink-0`} data-testid="tab-submissions"><Inbox className="h-4 w-4 mr-2" /> Submissions</TabsTrigger>
          </TabsList>
          <TabsContent value="events"><EventsPanel /></TabsContent>
          <TabsContent value="archives"><ArchivesPanel /></TabsContent>
          <TabsContent value="blog"><BlogPanel /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsPanel /></TabsContent>
          <TabsContent value="gallery"><GalleryPanel /></TabsContent>
          <TabsContent value="subscribers"><SubscribersPanel /></TabsContent>
          <TabsContent value="submissions"><SubmissionsPanel /></TabsContent>
        </Tabs>

        <div className="mt-10 text-xs text-zinc-600 flex items-center gap-2">
          <ExternalLink className="h-3 w-3" />
          <span>All changes appear on the public site immediately.</span>
        </div>
      </main>
    </div>
  );
}
